import { createInventoryItem, resolveItemTypeToMeta } from "game-common/item/item"
import { randomAppearanceFor } from "game-common/lpc/lpc"
import { interactionLabel, rankInteractions } from "game-common/mechanics/entity_mechanics"
import { Entity } from "game-common/models"
import { forestGrassTile } from "game-common/room/ember/grass_art"
import { degrees_to_radians } from "game-common/util"
import { BLEND_MODES, Container, Graphics, LINE_CAP, LINE_JOIN, Sprite, Texture } from "pixi.js"
import { isMobile } from "../../../../client_util"
import { contentSheetResource } from "../content_textures"
import { getRendererApp } from "../renderer_app"
import { FireSprite } from "../sprites/fire_sprite"
import { LpcComposite } from "../sprites/lpc_composite"
import { REWARD_GOLD, glowDisc, sparkBurst } from "./chapter_reward_art"
import { easeInCubic, easeInOutCubic, easeOutCubic, quadPoint, span } from "./chapter_reward_motion"
import { CommandWheel } from "./command_wheel"
import { FlameBackdrop } from "./flame_backdrop"
import { DemoCursor, DemoStamp, drawDioramaFrame, drawRipples, Pt, TutorialDemo } from "./tutorial_demo"

// The forager's wheel, shown once the chapter-three forager has walked in to the fire: a pointer
// right-clicks him (on a touch screen, holds on him), his wheel opens, Orders turns it to the jobs
// he can be given, and Feed Fire sends him to the hearth. Chapter three is that lesson twice over --
// ask him to feed the fire, ask him to repair the walls -- and nothing on screen says the wheel is
// there until somebody opens it.
//
// As with the building helper (building_tutorial.ts), what is inside the frame is the REAL thing:
// the man is an LPC body in the recruit's own look, lit by the game's hover outline; the wheel is
// the HUD's CommandWheel, bound exactly as GuiManager binds it (his verbs ranked by the same rule,
// then the forager's own orders page) and worked by hand through its hoverAt/pressAt, so its light,
// needle and pick flash are the ones the player will see. The campfire is the camp's own tiles.

const CELL = 32
const COLS = 10, ROWS = 6
const SCENE_W = COLS * CELL, SCENE_H = ROWS * CELL
const WIDTH = SCENE_W + 44
const SCENE_X = (WIDTH - SCENE_W) / 2
const SCENE_Y = 92
const CAPTION_Y = SCENE_Y + SCENE_H + 13
const CAPTION_H = 36
const HEIGHT = CAPTION_Y + CAPTION_H + 10

// The hearth: the campfire's three composite tiles (lpc-terrain:760 is the one a placed campfire
// names; its neighbours either side complete the art), logs centred on the middle one, and the
// camp's own flame standing in them.
const FIRE_COL = 1, FIRE_ROW = 3
const FIRE_X = (FIRE_COL + 1.5) * CELL
const FIRE_BASE_Y = FIRE_ROW * CELL + 20
// Without a renderer to bake the real flame (a test), the roster card's live one stands in.
const FLAME_W = 34, FLAME_H = 44
// The forager: standing a few paces off the fire, facing out. A body is drawn about its HIP; the
// soles are FEET_BELOW_HIP under it (see build_page's FEET_OFFSET).
const FEET_BELOW_HIP = 28
const FORAGER_X = 5.5 * CELL
const FORAGER_HIP_Y = 4 * CELL + 4 - FEET_BELOW_HIP
// The wheel opens over him as it does in the camp, a little smaller so his six orders fit the frame.
const WHEEL_RADIUS = 64 // the HUD's COMMAND_WHEEL_RADIUS
// Firelight is ADDED over grass, so it is starved of green: a gold glow there turns lime.
const FIRELIGHT = 0xff3d08 // FORGE.blaze, the camp's additive-only fire colour
const WHEEL_SCALE = 0.86
// The verbs a hired forager offers (lib/member_verbs.js), and the order this demo gives.
const MEMBER_VERBS = rankInteractions([{ type: "command" }, { type: "food" }, { type: "inventory" }])
const ORDERS = "command"
const FEED_FIRE = "forager:feed_fire"
const DEMO_ENTITY = "forager-tutorial"

// ---- The timeline ------------------------------------------------------------------------------

export const FORAGER_DEMO_MS = 6800
const ARRIVE_AT = 950 // the pointer is on him, and he lights up
const MENU_AT = 1450 // his wheel opens
const RIGHT_CLICK_AT = MENU_AT - 70
const HOLD_FROM = ARRIVE_AT // on a touch screen the press is held until the wheel answers
const PICK_ORDERS_AT = 2500
const ORDERS_MENU_AT = 2950
const PICK_FEED_AT = 4200
const TURN_AT = 4550 // he faces the fire...
const TOSS_FROM = 4650, TOSS_TO = 5050 // ...and throws a log on it
const STAMP_AT = 5350
const DONE_AT = 5550
const REPLAY_AT = 6200
const PRESS_MS = 120

const caption = (touch: boolean) => [
    touch ? "Touch and hold the forager to open his wheel." : "Right-click the forager (or tap with two fingers) to open his wheel.",
    "Pick Orders to see the jobs he can do.",
    "Choose a job. Feed Fire keeps the hearth burning.",
]
const doneCaption = (touch: boolean) => touch
    ? "Hire him with food, then hold on him for orders."
    : "Hire him with food, then right-click him for orders."

/** Where the demonstration is at `elapsedMs` into one play-through. */
export const foragerTutorialPose = (elapsedMs: number) => {
    const t = Math.min(FORAGER_DEMO_MS, Math.max(0, elapsedMs))
    return {
        step: t < MENU_AT ? 0 : t < PICK_ORDERS_AT ? 1 : 2,
        // The hover outline, from the pointer landing on him until the wheel covers him.
        highlighted: t >= ARRIVE_AT && t < MENU_AT + 150,
        turned: t >= TURN_AT,
        toss: span(t, TOSS_FROM, TOSS_TO),
        flare: t >= TOSS_TO ? 1 - easeOutCubic(span(t, TOSS_TO, TOSS_TO + 1400)) : 0,
        done: t >= DONE_AT,
    }
}

export interface ForagerCursorStops { rest: Pt; body: Pt; orders: Pt; feed: Pt }

// A hand moves in arcs, not along a ruler.
const glide = (from: Pt, to: Pt, k: number) =>
    quadPoint(from, { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 18 }, to, easeInOutCubic(k))

/** The pointer: onto him, onto Orders, onto Feed Fire, then out of the way of the fire. */
export const foragerCursorPose = (elapsedMs: number, at: ForagerCursorStops, touch = false): DemoCursor => {
    const t = Math.min(FORAGER_DEMO_MS, Math.max(0, elapsedMs))
    let p: Pt
    if (t < MENU_AT + 300) p = glide(at.rest, at.body, span(t, 150, ARRIVE_AT - 50))
    else if (t < ORDERS_MENU_AT) p = glide(at.body, at.orders, span(t, MENU_AT + 300, PICK_ORDERS_AT - 250))
    else if (t < PICK_FEED_AT + 120) p = glide(at.orders, at.feed, span(t, ORDERS_MENU_AT + 300, PICK_FEED_AT - 300))
    else {
        const k = easeInOutCubic(span(t, PICK_FEED_AT + 120, PICK_FEED_AT + 600))
        p = { x: at.feed.x + 18 * k, y: at.feed.y + 26 * k }
    }
    const pressedFrom = touch ? HOLD_FROM : RIGHT_CLICK_AT
    const pressed = (t >= pressedFrom && t < MENU_AT + (touch ? 80 : PRESS_MS - 70)) ||
        [PICK_ORDERS_AT, PICK_FEED_AT].some(at => t >= at && t < at + PRESS_MS)
    return {
        ...p,
        pressed,
        alpha: Math.min(easeOutCubic(span(t, 0, 250)), 1 - easeInCubic(span(t, PICK_FEED_AT + 250, PICK_FEED_AT + 750))),
    }
}

/** The recruit's own look (ember-recruit.js entitySetup), carrying an axe. */
const recruitBody = (): Entity => {
    const body = new Entity()
    body.movement.angle = degrees_to_radians(90)
    body.weapon = { weaponType: "none" }
    body.appearance = { ...randomAppearanceFor(undefined, "human", "male"), hair: "plain", hairColor: "black" }
    const skirts = resolveItemTypeToMeta("skirts", "brown" as any) || resolveItemTypeToMeta("skirts")
    body.inventory = { items: [
        { ...createInventoryItem(resolveItemTypeToMeta("axe"), { id: "forager-tutorial-axe", quantity: 1 }), active: true, readied: true },
        { ...createInventoryItem(skirts, { id: "forager-tutorial-skirts", quantity: 1 }), active: true },
    ], abilities: {}, lastUpdateTs: 0 }
    return body
}

/** Real game art, body and wheel in an isolated scene. The wheel's picks go nowhere. */
export class ForagerTutorial extends TutorialDemo {
    private scene = new Container()
    private frame = new Graphics()
    private hearthGlow = new Graphics()
    private flame: FireSprite | FlameBackdrop
    private forager = new LpcComposite("")
    private body = recruitBody()
    private wheelLayer = new Container()
    private wheel: CommandWheel
    // Normal-blend marks over the wheel: the mouse with its lit button (or the hold ring), the log.
    private hand = new Graphics()
    private fx = new Graphics()
    private stamp = new DemoStamp("ORDER GIVEN")
    private art: { sprite: Sprite; sheet: string; tile: number }[] = []
    private stops: ForagerCursorStops
    // Which wheel events the play-through has already played, so each happens once.
    private played = { menu: false, pickOrders: false, ordersMenu: false, pickFeed: false }
    // What the wheel reported picked this play-through. It goes nowhere else.
    private picked: string[] = []
    private highlighted?: boolean
    private turned = false

    constructor(hover: (over: boolean) => void, dismiss: () => void, touch = isMobile()) {
        super({
            width: WIDTH, height: HEIGHT, padX: SCENE_X, contentWidth: SCENE_W,
            captionY: CAPTION_Y, captionH: CAPTION_H, overline: "FORAGER", title: "Give him orders",
            steps: {
                labels: [touch ? "HOLD" : "RIGHT-CLICK", "ORDERS", "CHOOSE"],
                litAt: [0, MENU_AT + 420, PICK_ORDERS_AT + 420],
                doneAt: [MENU_AT, PICK_ORDERS_AT, PICK_FEED_AT],
                linkFill: [[MENU_AT + 60, MENU_AT + 420], [PICK_ORDERS_AT + 60, PICK_ORDERS_AT + 420]],
            },
            durationMs: FORAGER_DEMO_MS, replayAt: REPLAY_AT,
        }, hover, dismiss)
        this.touch = touch

        this.scene.position.set(SCENE_X, SCENE_Y)
        this.content.addChild(this.scene)
        for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
            this.addTile("forest-grass", forestGrassTile(x, y), x, y)
        }
        this.hearthGlow.blendMode = BLEND_MODES.ADD
        this.scene.addChild(this.hearthGlow)
        ;[759, 760, 761].forEach((tile, i) => this.addTile("lpc-terrain", tile, FIRE_COL + i, FIRE_ROW))
        this.flame = this.buildFlame()
        this.scene.addChild(this.flame)
        this.forager.position.set(FORAGER_X, FORAGER_HIP_Y)
        this.scene.addChild(this.forager)
        this.forager.updatePaperdoll(this.body)
        this.forager.doMovement(this.body)
        drawDioramaFrame(this.frame, SCENE_X, SCENE_Y, SCENE_W, SCENE_H)
        this.content.addChild(this.frame)

        // The wheel opens on him, and only the demonstration works it: no pointer reaches it.
        this.wheelLayer.position.set(SCENE_X + FORAGER_X, SCENE_Y + FORAGER_HIP_Y)
        this.wheelLayer.scale.set(WHEEL_SCALE)
        this.wheelLayer.interactiveChildren = false
        this.content.addChild(this.wheelLayer)
        this.wheel = this.buildWheel()
        this.stops = this.cursorStops()

        this.fx.blendMode = BLEND_MODES.ADD
        this.content.addChild(this.fx, this.hand)
        // In the clear row above the fire and the man, where the wheel was.
        this.stamp.place(SCENE_X + (FIRE_X + FORAGER_X) / 2, SCENE_Y + CELL * 0.7)
        this.content.addChild(this.stamp.burst, this.stamp.plaque)
        this.animate(0)
    }

    private addTile = (sheet: string, tile: number, x: number, y: number) => {
        const sprite = new Sprite(Texture.EMPTY)
        sprite.position.set(x * CELL, y * CELL)
        sprite.width = CELL; sprite.height = CELL
        this.scene.addChild(sprite)
        this.art.push({ sprite, sheet, tile })
    }

    private buildFlame = (): FireSprite | FlameBackdrop => {
        try {
            const fire = new FireSprite(getRendererApp().renderer)
            fire.position.set(FIRE_X, FIRE_BASE_Y + 4)
            return fire
        } catch {
            const flame = new FlameBackdrop(FLAME_W, FLAME_H, { glow: false })
            flame.position.set(FIRE_X - FLAME_W / 2, FIRE_BASE_Y - FLAME_H)
            return flame
        }
    }

    private buildWheel = () => {
        const wheel = new CommandWheel({ radius: WHEEL_RADIUS, onCommandSelect: command => this.picked.push(command) })
        wheel.visible = false
        this.wheelLayer.addChild(wheel)
        return wheel
    }

    // GuiManager.showInteractionWheel's binding, for a hired forager.
    private bindVerbs = () => this.wheel.bindToInteractions(DEMO_ENTITY,
        MEMBER_VERBS.map(verb => ({ name: verb.type, displayName: interactionLabel(verb) })))

    // ...and the forager's orders page, which the server opens when Orders is picked.
    private bindOrders = () => this.wheel.bindToEntity(DEMO_ENTITY, "forager")

    /** Aim by the real wheel's layout: lay each page out once, read where its slice is, put it away. */
    private cursorStops = (): ForagerCursorStops => {
        const toBoard = (aim: Pt | undefined): Pt => ({
            x: this.wheelLayer.x + (aim?.x ?? 0) * WHEEL_SCALE,
            y: this.wheelLayer.y + (aim?.y ?? 0) * WHEEL_SCALE,
        })
        this.bindVerbs()
        const orders = toBoard(this.wheel.aimAt(ORDERS))
        this.bindOrders()
        const feed = toBoard(this.wheel.aimAt(FEED_FIRE))
        return {
            rest: { x: SCENE_X + 8.4 * CELL, y: SCENE_Y + 1.4 * CELL },
            // His chest, a little above the hip the wheel opens on.
            body: { x: this.wheelLayer.x + 2, y: this.wheelLayer.y - 6 },
            orders,
            feed,
        }
    }

    protected rewind() {
        this.wheel.destroy({ children: true })
        this.wheel = this.buildWheel()
        this.played = { menu: false, pickOrders: false, ordersMenu: false, pickFeed: false }
        this.picked = []
    }

    protected cursorAt(t: number) {
        return foragerCursorPose(t, this.stops, this.touch)
    }

    protected captionAt(t: number) {
        const pose = foragerTutorialPose(t)
        return { text: pose.done ? doneCaption(this.touch) : caption(this.touch)[pose.step], done: pose.done }
    }

    protected poseScene(t: number, deltaMs: number) {
        const pose = foragerTutorialPose(t)
        this.art.forEach(({ sprite, sheet, tile }) => {
            const texture = contentSheetResource(sheet)?.textures?.[`${sheet}-${tile}`] || Texture.EMPTY
            if (sprite.texture !== texture) { sprite.texture = texture; sprite.width = CELL; sprite.height = CELL }
        })
        // A burning fire, and one that roars up when he feeds it.
        const strength = 0.6 + 0.4 * pose.flare
        if (this.flame instanceof FireSprite) this.flame.setIntensity(strength)
        else this.flame.update(deltaMs || 0, strength)
        this.hearthGlow.clear()
        const flicker = 0.5 + 0.5 * Math.sin(this.clock / 170) * Math.sin(this.clock / 310)
        glowDisc(this.hearthGlow, FIRE_X, FIRE_BASE_Y - 4, 40 + 10 * pose.flare, FIRELIGHT,
            0.14 + 0.04 * flicker + 0.22 * pose.flare, 5)

        // The hover outline belongs to the body's sprite, which loads after us and is rebuilt on a
        // new look: ask again until it takes.
        if (this.highlighted !== pose.highlighted) {
            this.highlighted = this.forager.setHighlighted(pose.highlighted) ? pose.highlighted : undefined
        }
        if (this.turned !== pose.turned) {
            this.turned = pose.turned
            this.body.movement.angle = degrees_to_radians(pose.turned ? 180 : 90)
            this.forager.doMovement(this.body)
        }

        this.playWheel(t)
        this.wheel.animate(deltaMs)
        this.drawHand(t, pose)
        this.drawFx(t, pose)
        this.stamp.draw(t - STAMP_AT)
    }

    /** Open, aim and pick on the real wheel, each once, as a player's hand would. */
    private playWheel = (t: number) => {
        const local = (ms: number) => {
            const p = this.cursorAt(ms)
            return { x: (p.x - this.wheelLayer.x) / WHEEL_SCALE, y: (p.y - this.wheelLayer.y) / WHEEL_SCALE }
        }
        // A pick lands where the pointer was at the moment of the pick, whatever frame it is seen in.
        const pick = (at: number) => {
            const p = local(at)
            this.wheel.hoverAt(p.x, p.y)
            this.wheel.pressAt(p.x, p.y)
            // GuiManager.hideCommandWheel: the pick flashes, holds a beat and lifts away.
            this.wheel.fadeOut()
            this.wheel.clearSelection()
        }
        if (t >= MENU_AT && !this.played.menu) {
            this.played.menu = true
            this.bindVerbs()
            this.wheel.fadeIn()
        }
        if (t >= PICK_ORDERS_AT && !this.played.pickOrders) {
            this.played.pickOrders = true
            pick(PICK_ORDERS_AT)
        }
        if (t >= ORDERS_MENU_AT && !this.played.ordersMenu) {
            this.played.ordersMenu = true
            this.bindOrders()
            this.wheel.fadeIn()
        }
        if (t >= PICK_FEED_AT && !this.played.pickFeed) {
            this.played.pickFeed = true
            pick(PICK_FEED_AT)
        }
        if (this.wheel.isOpen()) {
            const p = local(t)
            this.wheel.hoverAt(p.x, p.y)
        }
    }

    /**
     * Beside the pointer, which button it is: a small mouse with its RIGHT button lit as it clicks.
     * On a touch screen, a ring that fills round the fingertip while the press is held. Then the log
     * he throws on the fire.
     */
    private drawHand = (t: number, pose: ReturnType<typeof foragerTutorialPose>) => {
        const g = this.hand.clear()
        const cursor = this.cursorAt(t)
        const shown = Math.min(easeOutCubic(span(t, ARRIVE_AT - 250, ARRIVE_AT)), 1 - easeInCubic(span(t, MENU_AT + 250, MENU_AT + 550)))
        if (shown > 0 && this.touch) {
            const fill = span(t, HOLD_FROM, MENU_AT)
            if (fill > 0 && fill < 1) {
                g.lineStyle({ width: 3, color: 0x000000, alpha: 0.4 * shown }).drawCircle(cursor.x, cursor.y, 14)
                g.lineStyle({ width: 2.5, color: REWARD_GOLD, alpha: shown, cap: LINE_CAP.ROUND })
                g.moveTo(cursor.x, cursor.y - 14).arc(cursor.x, cursor.y, 14, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * fill)
                g.lineStyle(0)
            }
        } else if (shown > 0) {
            const lit = t >= RIGHT_CLICK_AT && t < MENU_AT + 260 ? 1 - easeInCubic(span(t, MENU_AT + 60, MENU_AT + 260)) : 0
            this.drawMouse(g, cursor.x + 15, cursor.y + 13, lit, shown)
        }
        if (pose.toss > 0 && pose.toss < 1) {
            // From his hands to the fire, end over end.
            const from = { x: FORAGER_X - 8, y: FORAGER_HIP_Y - 2 }, to = { x: FIRE_X, y: FIRE_BASE_Y - 6 }
            const p = quadPoint(from, { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 34 }, to, pose.toss)
            const a = -pose.toss * Math.PI * 2.2
            const ux = Math.cos(a) * 7, uy = Math.sin(a) * 7
            const x = SCENE_X + p.x, y = SCENE_Y + p.y
            g.lineStyle({ width: 6, color: 0x2a170b, alpha: 1, cap: LINE_CAP.ROUND }).moveTo(x - ux, y - uy).lineTo(x + ux, y + uy)
            g.lineStyle({ width: 4, color: 0x8a5a32, alpha: 1, cap: LINE_CAP.ROUND }).moveTo(x - ux, y - uy).lineTo(x + ux, y + uy)
            g.lineStyle(0)
            g.beginFill(0xd9b07a, 1).drawCircle(x + ux, y + uy, 1.6).endFill()
        }
    }

    private drawMouse = (g: Graphics, x: number, y: number, lit: number, alpha: number) => {
        const w = 11, h = 15, r = 5
        g.beginFill(0x000000, 0.35 * alpha).drawRoundedRect(x + 1, y + 1.5, w, h, r).endFill()
        g.lineStyle({ width: 1.2, color: 0x1a120c, alpha, join: LINE_JOIN.ROUND })
        g.beginFill(0xf4ead8, alpha).drawRoundedRect(x, y, w, h, r).endFill()
        // The right button, lit gold while it is pressed.
        if (lit > 0) {
            g.lineStyle(0)
            g.beginFill(REWARD_GOLD, alpha * lit)
            g.moveTo(x + w / 2 + 0.6, y + 0.8).lineTo(x + w - r, y + 0.8).arc(x + w - r, y + r, r - 0.8, -Math.PI / 2, 0)
                .lineTo(x + w - 0.8, y + 6).lineTo(x + w / 2 + 0.6, y + 6).closePath()
            g.endFill()
        }
        g.lineStyle({ width: 1, color: 0x1a120c, alpha })
        g.moveTo(x + w / 2, y + 0.5).lineTo(x + w / 2, y + 6)
        g.moveTo(x + 0.5, y + 6).lineTo(x + w - 0.5, y + 6)
        g.lineStyle(0)
    }

    /** Ripples where the pointer pressed, and the hearth taking the log. */
    private drawFx = (t: number, pose: ReturnType<typeof foragerTutorialPose>) => {
        const g = this.fx.clear()
        drawRipples(g, t, [this.touch ? MENU_AT : RIGHT_CLICK_AT, PICK_ORDERS_AT, PICK_FEED_AT],
            at => foragerCursorPose(at, this.stops, this.touch))
        if (t < TOSS_TO) return
        const ms = t - TOSS_TO
        const x = SCENE_X + FIRE_X, y = SCENE_Y + FIRE_BASE_Y - 10
        const bloom = 1 - easeOutCubic(span(ms, 0, 700))
        if (bloom > 0) glowDisc(g, x, y, 34, FIRELIGHT, 0.45 * bloom, 5)
        sparkBurst(g, x, y, span(ms, 0, 650), 12, 8, 34, REWARD_GOLD, 0.7, 1.5)
        // The light reaches him as well: he is the one who fed it.
        if (pose.flare > 0) glowDisc(g, SCENE_X + FORAGER_X, SCENE_Y + FORAGER_HIP_Y, 22, FIRELIGHT, 0.1 * pose.flare, 4)
    }

    protected teardown() {
        if (this.highlighted) this.forager.setHighlighted(false)
        this.forager.cleanup()
        // The real flame plays on the shared ticker; its baked frames are shared and stay.
        if (this.flame instanceof FireSprite) this.flame.cleanup()
    }
}
